import { Component, HostListener, inject, Inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Subject } from 'rxjs';
import { LocalStorageService } from './connectors/localstorage';
import { BehaviorSubject } from 'rxjs';
import { UserBehaviorService } from './services/user-events/user-behavior.service';
import { LoginService } from './services/login.service';
import { UsersService } from './services/users.service';
import { CryptService } from './services/crypt.service';
import { distinctUntilChanged, take, takeUntil, filter } from 'rxjs/operators';
import { ActivityService } from './services/auth/activityService.service';
import { AuthService } from './services/auth/authService.service';
import { RouterOutlet } from '@angular/router';
import { HeaderComponent } from './home/shared/components/header/header.component';
import { SideMenuComponent } from './home/shared/components/side-menu/side-menu.component';
import { MenuStateService } from './home/core/services/menu-state.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { NotificationsService } from '@core/notifications/services/notifications.service';
import { PageTitleService } from '@core/services/page-title.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  standalone: true,
  imports: [SideMenuComponent, RouterOutlet, HeaderComponent]
})
export class AppComponent implements OnInit, OnDestroy {
  readonly menuStateService = inject(MenuStateService);
  readonly notificationService = inject(NotificationsService);
  isMenuOpen = toSignal(this.menuStateService.isMenuOpen$, { initialValue: false });

  globalListenFunc!: Function;

  url: string = this.document.location.href;
  loginPath: boolean = false;

  base_url = window.location.origin;
  pathName = window.location.pathname.split('/');

  loaderSpin: boolean = false;

  timeLeft: number = 20000;
  interval: any;

  user: any;
  email: any;

  innerWidth: any;
  websocketUrl: any;

  downloadable: boolean = false;

  previousUrl: string = '';

  destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private localstorage: LocalStorageService,
    private renderer: Renderer2,
    private userBehavior: UserBehaviorService,
    private loginService: LoginService,
    private userService: UsersService,
    private crypt: CryptService,
    private activityService: ActivityService,
    private tokenLogin: AuthService,
    private pageTitleService: PageTitleService
  ) {}

  ngOnInit(): void {
    // Validate access when user logs in
    this.userBehavior.selectedUser.pipe(takeUntil(this.destroy$), distinctUntilChanged()).subscribe((user) => {
      if (user && user?.email) {
        this.email = user?.email;

        this.validateAccess();
      }

      this.chartUrl(this.url);
    });

    this.userBehavior.localStorageEvents$
      .pipe(
        filter((event) => event.userId),
        take(1)
      )
      .subscribe(() => {
        const userId = Number(this.localstorage.decryptAndGet('userId'));
        this.notificationService.initialize(userId);
      });

    this.globalListenFunc = this.renderer.listen('document', 'mousemove', (e) => {
      this.expiring();
    });

    this.globalListenFunc = this.renderer.listen('document', 'touchstart', (e) => {
      this.expiring();
    });

    this.globalListenFunc = this.renderer.listen('document', 'touchmove', (e) => {
      this.expiring();
    });

    this.globalListenFunc = this.renderer.listen('document', 'touchend', (e) => {
      this.expiring();
    });

    this.chartUrl(this.url);
  }

  validateAccess() {
    this.validateTeams();
    this.validateRoles();
    this.profileCheck();
    this.getUsersByEmail();
  }

  validateTeams(): any {
    this.userService.getUserAccess(this.email).subscribe((resp) => {
      const teams = [
        ...new Set(resp.map((item: any) => (item.statusFlag === '1' ? this.crypt.encryptBy(item.teamName) : null)))
      ];
      if (teams) {
        this.localstorage.set('teams-access', teams);

        this.userBehavior.emitTeamsEvent(true);
      }
    });
  }

  validateRoles(): any {
    this.userService.getUserRoleAccess(this.email).subscribe((resp) => {
      const roles = [
        ...new Set(resp.map((item: any) => (item.statusFlag === '1' ? this.crypt.encryptBy(item.roleName) : null)))
      ];
      if (roles) {
        this.localstorage.set('roles-access', roles);

        this.userBehavior.emitRolesEvent(true);
      }
    });
  }

  profileCheck() {
    this.userService.getProfile(this.email).subscribe((resp) => {
      //console.log('resp')
      let cryptedUser = '';
      if (resp.profile.length > 0) {
        cryptedUser = this.crypt.encryptArray(resp.profile);
      } else {
        cryptedUser = this.crypt.encryptBy(resp.profile);
      }

      if (cryptedUser) this.localstorage.set('profile', [cryptedUser]);
    });
  }

  chartUrl(url: string) {
    const pathName = window.location.pathname.split('/');

    if (url.includes('/login') || pathName[1] == '') {
      this.loginPath = true;
    } else {
      this.loginPath = false;
    }
  }

  setLoader(url: string) {
    if (
      url.includes('/projects/details') ||
      url.includes('/teams') ||
      url.includes('/roles') ||
      url.includes('/users')
    ) {
      this.loaderSpin = !this.loaderSpin;

      setTimeout(() => {
        this.loaderSpin = false;
      }, 8000);
    }
  }

  expiring(expire?: boolean) {
    const subject = new BehaviorSubject(this.timeLeft);

    if (!expire) {
      this.timeLeft = 20000;
      this.startTimer();
    }

    if (subject.value == 0 || expire) {
      const local = this.localstorage.get('NTTUSLOGIN');
      if (local) {
        this.localstorage.remove('NTTUSLOGIN');
        this.loginService.expire(this.email, 'Y').subscribe();
      }
    }
  }

  startTimer() {
    this.timeLeft = 20000;
    this.interval = setInterval(() => {
      if (this.timeLeft > 0) {
        this.timeLeft--;
        if (this.timeLeft == 0) {
          this.expiring(true);
        }
      } else {
        this.timeLeft = 20000;
      }
    }, 1000);
  }

  async getUsersByEmail() {
    const user = await this.userBehavior.fromBehavior();
    this.userService.getUsersByEmail(user?.email).subscribe((resp) => {
      const cryptedUser = this.crypt.encryptBy(resp[0].userId.toString());

      if (cryptedUser) {
        this.localstorage.set('userId', [cryptedUser]);

        this.userBehavior.emitUserIdEvent(true);
      }

      this.activityService.setupActivityListeners();
      this.tokenLogin.login(user?.email);
    });
  }

  @HostListener('window:resize', ['$event'])
  listenerWidth(event: any) {
    this.innerWidth = window.innerWidth;
    if (this.innerWidth > 1920) {
      location.reload();
    }
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();

    this.globalListenFunc();
  }
}
